import clsx from 'clsx';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { valueHandler } from '../../../../../../_metronic/helpers';
import { api } from '../../../../../modules/api/api';
import StatusBadge from '../../../../../modules/components/badge/StatusBadge';
import CopyTextButton from '../../../../../modules/components/copyText/CopyTextButton';
import Time from '../../../../../modules/components/time/Time';
import { useAuth } from '../../../../../modules/auth';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { SiService } from '../../../mappings';
import { parseSiRequestStatus } from '../utils';

interface Props {
  data?: any;
  isEditable: boolean;
}

export default function BasicInformation({ data, isEditable }: Props) {
  const { auth, refresh } = useAuth();
  const intl = useIntl();
  const { setToast } = useToast();
  const t = (id: string) => intl.formatMessage({ id: `SI_BASIC_INFORMATION.${id}` });
  const emDash = '—';
  const domainResults =
    data?.sites?.find((item: any) => item?.service === SiService.DOMAIN_SIFT_SERVICE) ?? {};
  const requestId = data?.request_id;
  const requestUrl = data?.queries?.[0]?.query?.value;

  const defaultRedirectHistory =
    domainResults?.result?.geo_info?.results?.default?.redirect_history;
  const defaultRedirectUrl = defaultRedirectHistory?.[defaultRedirectHistory.length - 1]?.url ?? null;

  const redirectUrl =
    domainResults?.query?.value !== defaultRedirectUrl
      ? defaultRedirectUrl
      : null;

  const urlCountByRequestId = useQuery(
    ['site-intelligence-url-count-by-request-id', requestId],
    async () => {
      try {
        if (!requestId) {
          return [];
        }

        const { result, status } = await api(
          `admin/v1/get_url_count_by_request_id`,
          {
            method: 'POST',
            headers: {
              'content-type': 'application/json',
            },
            body: JSON.stringify({
              request_id: [requestId],
            }),
          },
          { token: auth?.accessToken, refresh },
          'site-intelligence'
        ).then(async (resp) => {
          const result = await resp?.json();
          return { result: result, status: result?.code ?? resp?.status };
        });

        if (status === 200 && result?.code === 200 && result?.error === false) {
          return result?.results ?? [];
        }

        return [];
      } catch (err) {
        const response = await err?.response?.json();
        setToast({
          show: true,
          type: 'Danger',
          header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
          message: response?.message ?? err.message,
        });
        return [];
      }
    },
    {
      enabled: !!requestId && isEditable,
      keepPreviousData: true,
    }
  );

  const requestUrlCountItem = Array.isArray(urlCountByRequestId?.data)
    ? urlCountByRequestId.data.find((item) => item?.request_id === requestId)
    : null;
  const hasRequestUrlCount = typeof requestUrlCountItem?.count === 'number';
  const requestUrlCount = hasRequestUrlCount ? requestUrlCountItem?.count : emDash;

  function changeStatusColor(status: string) {
    const { label, color } = parseSiRequestStatus(status);
    return (
      <OverlayTrigger placement='top' overlay={<Tooltip>{label}</Tooltip>}>
        <span>
          <StatusBadge color={color} />
        </span>
      </OverlayTrigger>
    );
  }

  return (
    <div className='card card-custom card-flush border border-gray-300 border-dashed mb-9'>
      <div className='card-body py-2 px-4 d-flex justify-content-between align-items-center gap-2 fs-8'>
        <div className='row justify-content-between align-items-center flex-grow-1 g-2'>
          {/* <div className='col-auto'>
            <div className='text-muted fw-bold fs-7'>Company Name</div>
            <div className='text-dark fw-bolder fs-7'>
              {valueHandler(data?.request_info?.company_name)}
            </div>
          </div> */}
          <div className='col-auto'>
            <div className='text-muted fw-bold fs-7'>{t('GROUP_NAME')}</div>
            <div className='text-dark fw-bolder fs-7'>
              {valueHandler(data?.request_info?.group_name)}
            </div>
          </div>
          <div className='col-auto'>
            <div className='text-muted fw-bold fs-7'>{t('MERCHANT_NAME')}</div>
            <div className='text-dark fw-bolder fs-7'>
              {valueHandler(data?.request_info?.merchant_name)}
            </div>
          </div>
          <div className='col-auto'>
            <div className='text-muted fw-bold fs-7'>{t('DESCRIPTOR')}</div>
            <div className='text-dark fw-bolder fs-7'>
              {valueHandler(data?.request_info?.descriptor)}
            </div>
          </div>
          <div className='col-auto'>
            <div className='text-muted fw-bold fs-7'>{t('MID')}</div>
            <div className='text-dark fw-bolder fs-7'>{valueHandler(data?.request_info?.mid)}</div>
          </div>
          <div className='col-auto'>
            <div className='text-muted fw-bold fs-7'>{t('MCC')}</div>
            <div className='text-dark fw-bolder fs-7'>{valueHandler(data?.request_info?.mcc)}</div>
          </div>
          <div className='col-auto'>
            <div className='text-muted fw-bold fs-7'>
              {t('REPORT_DATE')} {changeStatusColor(data?.status)}
            </div>
            <div className='text-dark fw-bolder fs-7'>
              {data?.report_date ? (
                <Time
                  date={new Date(data?.report_date || data?.created_at)}
                  format='yyyy-MM-dd HH:mm:ss'
                  utc
                />
              ) : (
                emDash
              )}
            </div>
          </div>
          <div className='col-auto'>
            <div className='text-muted fw-bold fs-7'>{t('REQUEST_URL')}</div>
            <div className='text-dark fw-bolder fs-7 text-break'>
              <a
                href={requestUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='text-dark text-hover-primary'
              >
                {requestUrl}
              </a>
              <CopyTextButton text={requestUrl} />
              {isEditable && (
                <span
                  className={clsx(
                    'badge ms-1 px-2 py-1 fw-semibold fs-8 align-middle',
                    hasRequestUrlCount
                      ? 'badge-light-primary text-primary'
                      : 'badge-light text-muted'
                  )}
                  title='Same URL Submission Count'
                >
                  {requestUrlCount}
                </span>
              )}
            </div>
          </div>
          {redirectUrl && (
            <div className='col-auto'>
              <div className='text-muted fw-bold fs-7'>
                {t('REDIRECTED_URL')}{' '}
                <span className='badge badge-light-dark text-capitalize align-middle'>
                  {t('THIS_REPORT')}
                </span>
              </div>
              <div className='text-dark fw-bolder fs-7 text-break'>
                <a
                  href={redirectUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-dark text-hover-primary'
                >
                  {redirectUrl}
                </a>
              </div>
            </div>
          )}
          <div className='col-auto'></div>
        </div>
      </div>
    </div>
  );
}
